/**
 * docs/BILLING.md §9 — what a visitor's link carried, kept on this device until it is used.
 *
 * `quantphi.app/?c=CODE` (any page: `/register?c=CODE`, `/results?c=CODE`, `/billing?c=CODE`)
 * remembers a coupon code at page load (`main.tsx`), through sign-up, until the checkout dialog
 * fills it in. A code that turns out unknown or expired is dropped there without a word.
 *
 * The link's campaign tags (`utm_source`, `utm_medium`, `utm_campaign`, `ref`) are kept the same
 * way and sent once, with the sign-up (§9.2), so the administrator can count where accounts came
 * from. The last link that carried any of them wins; a visit without them changes nothing.
 *
 * Storage is best effort: private mode or blocked storage only means the code is not
 * remembered, never an error (`safeStorage` swallows every failure).
 */

import { safeStorage } from '@/lib/api';

const COUPON_KEY = 'qp.coupon';
const SOURCE_KEY = 'qp.signupSource';
/** The tags a link may carry, as the API takes them (`SignupSource`). */
const SOURCE_TAGS = ['utm_source', 'utm_medium', 'utm_campaign', 'ref'] as const;
/** What the API keeps of a tag (`users.signup_*`, 64 characters). */
const TAG_MAX = 64;
/** A remembered code is offered for this long after the link was opened. */
const COUPON_TTL_MS = 90 * 24 * 60 * 60 * 1000;
/** What a campaign code can be (`billing_campaigns.code`, 32 characters): nothing else is kept. */
const CODE = /^[A-Z0-9_-]{1,32}$/;

/** A code as the API writes it (`billing.normalise_code`: no spaces, upper case), or `null`. */
export function normaliseCoupon(raw: string | null | undefined): string | null {
  const code = (raw ?? '').replace(/\s+/g, '').toUpperCase();
  return CODE.test(code) ? code : null;
}

/** Keep `code` on this device for the checkout. */
export function rememberCoupon(code: string, now: number = Date.now()): void {
  const clean = normaliseCoupon(code);
  if (clean) safeStorage.set(COUPON_KEY, JSON.stringify({ code: clean, at: now }));
}

/** The code a link left here, unless it is older than `COUPON_TTL_MS`. */
export function rememberedCoupon(now: number = Date.now()): string | null {
  const raw = safeStorage.get(COUPON_KEY);
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as { code?: unknown; at?: unknown };
    const code = typeof saved.code === 'string' ? normaliseCoupon(saved.code) : null;
    const at = typeof saved.at === 'number' ? saved.at : 0;
    return code && now - at <= COUPON_TTL_MS ? code : null;
  } catch {
    return null;
  }
}

/** Drop the remembered code — only `code` when given, so a newer link's code survives. */
export function forgetCoupon(code?: string): void {
  if (code === undefined || rememberedCoupon() === normaliseCoupon(code)) {
    safeStorage.remove(COUPON_KEY);
  }
}

/** Read `?c=` (or `?coupon=`) off a page's query string and remember it. Returns the code kept. */
export function captureLinkCoupon(search: string, now: number = Date.now()): string | null {
  const params = new URLSearchParams(search);
  const code = normaliseCoupon(params.get('c') ?? params.get('coupon'));
  if (code) rememberCoupon(code, now);
  return code;
}

export type SourceTag = (typeof SOURCE_TAGS)[number];

/** What goes with the sign-up (`RegisterRequest.source`): the link's tags and its coupon. */
export type SignupSource = Partial<Record<SourceTag | 'coupon', string>>;

/** Read the link's campaign tags; kept only when it carries at least one (last link wins). */
export function captureLinkSource(search: string, now: number = Date.now()): SignupSource | null {
  const params = new URLSearchParams(search);
  const tags: SignupSource = {};
  for (const tag of SOURCE_TAGS) {
    const value = params.get(tag)?.trim().slice(0, TAG_MAX);
    if (value) tags[tag] = value;
  }
  if (Object.keys(tags).length === 0) return null;
  safeStorage.set(SOURCE_KEY, JSON.stringify({ ...tags, at: now }));
  return tags;
}

/** Everything a page load keeps from its link: the coupon and the campaign tags. */
export function captureLink(search: string, now: number = Date.now()): void {
  captureLinkCoupon(search, now);
  captureLinkSource(search, now);
}

/**
 * What the sign-up sends: the kept tags (unless older than the coupon's 90 days) and the kept
 * coupon. `undefined` when the visitor came through no link.
 */
export function signupSource(now: number = Date.now()): SignupSource | undefined {
  const source: SignupSource = {};
  const raw = safeStorage.get(SOURCE_KEY);
  if (raw) {
    try {
      const saved = JSON.parse(raw) as Record<string, unknown>;
      const at = typeof saved.at === 'number' ? saved.at : 0;
      if (now - at <= COUPON_TTL_MS) {
        for (const tag of SOURCE_TAGS) {
          const value = saved[tag];
          if (typeof value === 'string' && value) source[tag] = value.slice(0, TAG_MAX);
        }
      }
    } catch {
      /* junk in storage: as if nothing was kept */
    }
  }
  const coupon = rememberedCoupon(now);
  if (coupon) source.coupon = coupon;
  return Object.keys(source).length ? source : undefined;
}

/** After a sign-up: the tags have been told. The coupon stays for the checkout. */
export function forgetSignupSource(): void {
  safeStorage.remove(SOURCE_KEY);
}

/**
 * The codes a checkout quietly drops when a remembered one comes back with them
 * (`billing.quote_sync`'s `code_error`): it can never apply again. `not_for_this` is kept — it
 * may fit another package or period.
 */
export const DEAD_COUPON_ERRORS: readonly string[] = ['unknown', 'expired', 'used_up', 'already_used'];
